<template>
    <page title="registerTitle">
        <template #pageContainer>
            <form v-if="!isRegistered">
				<v-row><v-col>
                <v-text-field outlined dense
					hide-details="auto"
                    :disabled="loading"
                    v-model="firstName"
                    :label="$t('text.firstName')"
                    :error-messages="firstNameErrors"
                    required
                    @input="$v.firstName.$touch()"
                    @blur="$v.firstName.$touch()">
                </v-text-field>
				</v-col></v-row>
				<v-row><v-col>

                <v-text-field outlined dense
					hide-details="auto"
                    :disabled="loading"
                    v-model="lastName"
                    :label="$t('text.lastName')"
                    :error-messages="lastNameErrors"
                    required
                    @input="$v.lastName.$touch()"
                    @blur="$v.lastName.$touch()">
                </v-text-field>
				</v-col></v-row>
				<v-row><v-col>

                <v-text-field outlined dense
					hide-details="auto"
                    :disabled="loading"
                    v-model="email"
                    :label="$t('text.email')"
                    :error-messages="emailErrors"
                    required
                    @input="$v.email.$touch()"
                    @blur="$v.email.$touch()">
                </v-text-field>
				</v-col></v-row>
				<v-row><v-col>

                <v-text-field outlined dense
					hide-details="auto"
                    :disabled="loading"
                    v-model="password"
                    type="password"
                    :label="$t('text.password')"
                    :error-messages="passwordErrors"
                    required
                    @input="$v.password.$touch()"
                    @blur="$v.password.$touch()">
                </v-text-field>
				</v-col></v-row>
				<v-row><v-col>

                <v-text-field outlined dense
					hide-details="auto"
                    :disabled="loading"
                    v-model="confirmPassword"
                    type="password"
                    :label="$t('text.confirmPassword')"
                    :error-messages="confirmPasswordErrors"
                    required
                    @input="$v.confirmPassword.$touch()"
                    @blur="$v.confirmPassword.$touch()">
                </v-text-field>
				</v-col></v-row>
				<v-row><v-col>

                <v-select
					hide-details="auto"
                    :disabled="loading"
                    v-model="organization.selected"
                    :items="masterOrganizations"
                    :error-messages="organizationErrors"
                    :readonly="hasOnlyOneOrganization"
                    @input="$v.organization.selected.$touch()"
                    @blur="$v.organization.selected.$touch()"
                    required
                    :label="$t('text.masterorganization')">
                </v-select>
				</v-col></v-row>
				<v-row><v-col>

                <v-select
					hide-details="auto"
                    :disabled="loading"
                    v-model="language.selected"
                    :items="allLanguages"
                    :label="$t('text.language')"
                    :error-messages="languageErrors"
                    :readonly="hasOnlyOneLanguage"
                    required
                    @input="$v.language.selected.$touch()"
                    @blur="$v.language.selected.$touch()">
                </v-select>
				</v-col></v-row>
				<v-row><v-col>
					<c-checkbox v-model="newsletter" :text="$t('text.hintNewsletter')"></c-checkbox>
				</v-col></v-row>
				<v-row><v-col>
					<v-alert
						dense
						type="warning">
						{{$t('text.hintMustAgree')}}
					</v-alert>
				</v-col></v-row>

                <v-btn
                    class="mr-4"
                    :disabled="$v.$invalid || loading"
                    @click="register">
                    {{ $t(`text.registerButton`) }}
                </v-btn>
            </form>

            <v-alert v-else type="success">
                {{ $t(`text.registrationSuccess`) }}
            </v-alert>
            <spinner :loading="loading"></spinner>
        </template>
    </page>
</template>

<script>
import { mapActions } from 'vuex';
import { required, email } from 'vuelidate/lib/validators';

import authActions from '../store/actions/auth';
import organizationActions from '../store/actions/organization';
import languageActions from '../store/actions/language';
import namespace from '../store/namespace';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';
import CCheckbox from '../general/CCheckbox.vue';

import modelState from '../mixins/modelState';

export default {
	name: 'Register',
	mixins: [modelState],
	components: {
		Page,
		Spinner,
		CCheckbox,
	},
	validations: {
		firstName: {
			required,
		},
		lastName: {
			required,
		},
		email: {
			required,
			email,
		},
		password: {
			required,
		},
		confirmPassword: {
			required,
		},
		language: {
			selected: {
				required,
			},
		},
		organization: {
			selected: {
				required,
			},
		},
	},
	async mounted() {
		await this.getMasterOrganizations();

		await this.getLanguages();
	},
	data() {
		return {
			firstName: '',
			lastName: '',
			email: '',
			newsletter: false,
			password: '',
			confirmPassword: '',
			language: {
				items: [],
				selected: '1',
			},
			organization: {
				items: [],
				selected: '',
			},
			modelState: {},
			confirmed: false,
			loading: false,
		};
	},
	computed: {
		isRegistered() {
			return this.confirmed;
		},
		firstNameErrors() {
			const errors = [];

			if (!this.$v.firstName.$dirty) return errors;

			const apiErrors = this.validateProperty('FirstName');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.firstName.required && errors.push(this.$i18n.t('text.errorFirstNameRequired'));

			return errors;
		},
		lastNameErrors() {
			const errors = [];

			if (!this.$v.lastName.$dirty) return errors;

			const apiErrors = this.validateProperty('LastName');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.lastName.required && errors.push(this.$i18n.t('text.errorLastNameRequired'));

			return errors;
		},
		emailErrors() {
			const errors = [];

			if (!this.$v.email.$dirty) return errors;

			const apiErrors = this.validateProperty('Email');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.email.required && errors.push(this.$i18n.t('text.errorEmailRequired'));
			!this.$v.email.email && errors.push(this.$i18n.t('text.errorInvalidEmail'));

			return errors;
		},
		passwordErrors() {
			const errors = [];

			if (!this.$v.password.$dirty) return errors;

			const apiErrors = this.validateProperty('Password');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.password.required && errors.push(this.$i18n.t('text.errorPasswordRequired'));

			return errors;
		},
		confirmPasswordErrors() {
			const errors = [];

			if (!this.$v.confirmPassword.$dirty) return errors;

			const apiErrors = this.validateProperty('ConfirmPassword');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.confirmPassword.required && errors.push(this.$i18n.t('text.errorConfirmPasswordRequired'));

			return errors;
		},
		languageErrors() {
			const errors = [];

			if (!this.$v.language.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('LanguageId');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.language.selected.required && errors.push(this.$i18n.t('text.errorLanguageRequired'));

			return errors;
		},
		organizationErrors() {
			const errors = [];

			if (!this.$v.organization.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('OrganizationId');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.organization.selected.required && errors.push(this.$i18n.t('text.errorOrganizationRequired'));

			return errors;
		},
		masterOrganizations() {
			return this.organization.items;
		},
		allLanguages() {
			return this.language.items;
		},
		hasOnlyOneOrganization() {
			return this.organization.items.length === 1;
		},
		hasOnlyOneLanguage() {
			return this.organization.items.length === 1;
		},
	},
	methods: {
		...mapActions(namespace.AUTH, [authActions.REGISTER]),
		...mapActions(namespace.ORGANIZATION, [organizationActions.GET_MASTER_ORGANIZATIONS]),
		...mapActions(namespace.LANGUAGE, [languageActions.GET_LANGUAGES]),
		async register() {
			this.loading = true;
			this.$v.$touch();

			if (!this.$v.$invalid) {
				const newUser = {
					firstName: this.firstName,
					lastName: this.lastName,
					email: this.email,
					newsletter: this.newsletter,
					password: this.password,
					confirmPassword: this.confirmPassword,
					languageId: this.language.selected,
					organizationId: this.organization.selected,
					confirmUrl: `${window.location.origin}/confirm`,
				};

				try {
					this.modelState = {};
					await this.REGISTER(newUser);

					this.confirmed = true;
				} catch (e) {
					if (e.response.status === 422) {
						this.modelState = e.response.data;
						if (this.modelError()) {
							throw new Error(this.modelError());
						}
					}

					throw e;
				} finally {
					this.loading = false;
				}
			}
			this.loading = false;
		},
		async getMasterOrganizations() {
			this.organization.items = await this.GET_MASTER_ORGANIZATIONS();

			if (this.organization.items.length > 0) {
				this.organization.selected = this.organization.items[0].value;
			}
		},
		async getLanguages() {
			this.language.items = await this.GET_LANGUAGES();

			if (this.language.items.length > 0) {
				this.language.selected = this.language.items[0].value;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
form {
	margin-bottom: 10px;
}
</style>
